import React, { useEffect, useMemo, useState } from "react";
import {
  FileText, Image as ImageIcon, Loader2, RefreshCw, Clock,
  List as ListIcon, CalendarDays, ChevronLeft, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ReviewModal } from "@/components/blog/BulkBatchTab";

const STATUS_STYLE = {
  draft: "border-brand-amber/40 text-brand-amber bg-brand-amber/10",
  scheduled: "border-sky-400/40 text-sky-300 bg-sky-400/10",
  failed: "border-red-400/40 text-red-300 bg-red-400/10",
  generating: "border-line text-ink-muted bg-bg",
};

const MONTHS = ["January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"];
const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export default function DraftsTab() {
  const [drafts, setDrafts] = useState(null);
  const [review, setReview] = useState(null);
  const [view, setView] = useState("list");
  const [dragOver, setDragOver] = useState(null);
  const [month, setMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });

  const load = async () => {
    try {
      const { data } = await api.get("/blog-bulk/drafts");
      setDrafts(data.drafts || []);
    } catch { toast.error("Couldn't load drafts"); }
  };
  useEffect(() => { load(); }, []);

  const openReview = async (postId) => {
    try {
      const { data } = await api.get(`/blog-bulk/posts/${postId}`);
      setReview(data);
    } catch { toast.error("Couldn't load post"); }
  };

  const { byDay, unscheduled } = useMemo(() => {
    const map = {};
    const un = [];
    (drafts || []).forEach((d) => {
      if (d.scheduled_for) {
        const dt = new Date(d.scheduled_for);
        const key = `${dt.getFullYear()}-${dt.getMonth()}-${dt.getDate()}`;
        (map[key] = map[key] || []).push(d);
      } else {
        un.push(d);
      }
    });
    Object.values(map).forEach((arr) => arr.sort((a, b) => new Date(a.scheduled_for) - new Date(b.scheduled_for)));
    return { byDay: map, unscheduled: un };
  }, [drafts]);

  if (!drafts) {
    return (
      <div className="p-10 text-center text-sm text-ink-muted">
        <Loader2 className="h-5 w-5 mx-auto animate-spin text-brand-amber mb-2" />Loading drafts…
      </div>
    );
  }

  const today = new Date();
  const y = month.getFullYear(), m = month.getMonth();
  const daysInMonth = new Date(y, m + 1, 0).getDate();
  const startWeekday = new Date(y, m, 1).getDay();
  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const startDrag = (e, d) => {
    e.dataTransfer.setData("text/plain", JSON.stringify({ post_id: d.post_id, time: d.scheduled_for || null }));
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDrop = async (e, day, isPast) => {
    e.preventDefault();
    setDragOver(null);
    if (isPast) { toast.error("Can't schedule in the past"); return; }
    let payload;
    try { payload = JSON.parse(e.dataTransfer.getData("text/plain")); } catch { return; }
    if (!payload?.post_id) return;
    const prev = payload.time ? new Date(payload.time) : null;
    let when = new Date(y, m, day, prev ? prev.getHours() : 9, prev ? prev.getMinutes() : 0);
    if (when <= new Date()) when = new Date(Date.now() + 60 * 60 * 1000);
    try {
      await api.post(`/blog-bulk/posts/${payload.post_id}/schedule`, { scheduled_for: when.toISOString() });
      toast.success(`Scheduled for ${when.toLocaleString()}`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Couldn't reschedule"); }
  };

  return (
    <div className="mt-6" data-testid="drafts-tab">
      <div className="rounded-xl border border-line bg-bg-surface p-5">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink-muted">
            // Unpublished drafts — click to review, schedule or publish
          </div>
          <div className="flex items-center gap-2">
            <div className="flex rounded-md border border-line overflow-hidden">
              <button onClick={() => setView("list")} data-testid="drafts-view-list"
                className={`inline-flex items-center gap-1 px-2.5 py-1.5 text-[11px] transition ${view === "list" ? "bg-brand-amber/15 text-brand-amber" : "text-ink-muted hover:text-ink-primary"}`}>
                <ListIcon className="h-3 w-3" /> List
              </button>
              <button onClick={() => setView("calendar")} data-testid="drafts-view-calendar"
                className={`inline-flex items-center gap-1 px-2.5 py-1.5 text-[11px] transition ${view === "calendar" ? "bg-brand-amber/15 text-brand-amber" : "text-ink-muted hover:text-ink-primary"}`}>
                <CalendarDays className="h-3 w-3" /> Calendar
              </button>
            </div>
            <button onClick={load} data-testid="drafts-refresh-btn"
              className="inline-flex items-center gap-1 text-[11px] text-ink-muted hover:text-ink-primary transition">
              <RefreshCw className="h-3 w-3" /> Refresh
            </button>
          </div>
        </div>

        {drafts.length === 0 ? (
          <div className="py-10 text-center text-sm text-ink-muted" data-testid="drafts-empty">
            <FileText className="h-6 w-6 mx-auto mb-2 opacity-50" />
            No drafts yet — create posts in the Bulk Batch tab and they'll appear here until published.
          </div>
        ) : view === "list" ? (
          <div className="divide-y divide-line">
            {drafts.map((d) => (
              <button key={d.post_id} onClick={() => openReview(d.post_id)}
                data-testid={`draft-row-${d.post_id}`}
                className="w-full flex items-center gap-3 py-2.5 px-1 text-left hover:bg-bg-elevated/60 rounded-md transition group">
                {d.image_url ? (
                  <img src={d.image_url} alt="" className="h-9 w-14 object-cover rounded border border-line shrink-0" />
                ) : (
                  <span className="h-9 w-14 rounded border border-dashed border-line grid place-items-center shrink-0">
                    <ImageIcon className="h-3.5 w-3.5 text-ink-muted" />
                  </span>
                )}
                <span className="flex-1 min-w-0">
                  <span className="block text-sm text-ink-primary truncate group-hover:text-brand-amber transition">
                    {d.title || d.topic || "Untitled draft"}
                  </span>
                  <span className="block text-[10px] text-ink-muted font-mono">
                    updated {new Date(d.updated_at || d.created_at).toLocaleString()}
                    {d.scheduled_for && (
                      <> · <Clock className="h-2.5 w-2.5 inline -mt-0.5" /> publishes {new Date(d.scheduled_for).toLocaleString()}</>
                    )}
                  </span>
                </span>
                <span className={`shrink-0 rounded-full border px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider ${STATUS_STYLE[d.status] || STATUS_STYLE.generating}`}>
                  {d.status}
                </span>
              </button>
            ))}
          </div>
        ) : (
          <div data-testid="drafts-calendar">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-bold text-ink-primary" data-testid="drafts-calendar-month">
                {MONTHS[m]} {y}
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => setMonth(new Date(y, m - 1, 1))} data-testid="drafts-cal-prev"
                  className="p-1.5 rounded-md border border-line text-ink-muted hover:text-ink-primary transition"><ChevronLeft className="h-3.5 w-3.5" /></button>
                <button onClick={() => setMonth(new Date(today.getFullYear(), today.getMonth(), 1))} data-testid="drafts-cal-today"
                  className="px-2.5 py-1.5 rounded-md border border-line text-[11px] text-ink-muted hover:text-ink-primary transition">Today</button>
                <button onClick={() => setMonth(new Date(y, m + 1, 1))} data-testid="drafts-cal-next"
                  className="p-1.5 rounded-md border border-line text-ink-muted hover:text-ink-primary transition"><ChevronRight className="h-3.5 w-3.5" /></button>
              </div>
            </div>
            <div className="grid grid-cols-7 gap-px text-center mb-1">
              {DOW.map((d) => <div key={d} className="font-mono text-[9px] uppercase tracking-wider text-ink-muted py-1">{d}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-px bg-line rounded-lg overflow-hidden border border-line">
              {cells.map((day, i) => {
                if (day === null) return <div key={i} className="bg-bg min-h-[84px]" />;
                const key = `${y}-${m}-${day}`;
                const items = byDay[key] || [];
                const isToday = day === today.getDate() && m === today.getMonth() && y === today.getFullYear();
                const isPast = new Date(y, m, day, 23, 59) < today && !isToday;
                return (
                  <div key={i}
                    onDragOver={(e) => { if (!isPast) { e.preventDefault(); setDragOver(day); } }}
                    onDragLeave={() => setDragOver((v) => (v === day ? null : v))}
                    onDrop={(e) => handleDrop(e, day, isPast)}
                    className={`bg-bg-surface min-h-[84px] p-1 transition ${isPast ? "opacity-50" : ""} ${dragOver === day ? "ring-1 ring-inset ring-brand-amber bg-brand-amber/5" : ""}`}
                    data-testid={`drafts-cal-day-${day}`}>
                    <div className={`text-[10px] font-mono mb-1 ${isToday ? "inline-block px-1.5 rounded-full bg-brand-amber text-black font-bold" : "text-ink-muted"}`}>
                      {day}
                    </div>
                    <div className="space-y-0.5">
                      {items.slice(0, 3).map((d) => (
                        <button key={d.post_id} onClick={() => openReview(d.post_id)}
                          draggable onDragStart={(e) => startDrag(e, d)}
                          title={`${d.title} — ${new Date(d.scheduled_for).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} · drag to another day to reschedule`}
                          data-testid={`drafts-cal-chip-${d.post_id}`}
                          className={`w-full text-left truncate rounded px-1 py-0.5 text-[9.5px] border transition cursor-grab active:cursor-grabbing hover:brightness-125 ${STATUS_STYLE[d.status] || STATUS_STYLE.scheduled}`}>
                          {new Date(d.scheduled_for).toLocaleTimeString([], { hour: "numeric" })} · {d.title || d.topic}
                        </button>
                      ))}
                      {items.length > 3 && (
                        <div className="text-[9px] text-ink-muted px-1">+{items.length - 3} more</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-4">
              <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-ink-muted mb-2">
                // Unscheduled drafts — drag one onto a day, or open it and hit Schedule
              </div>
              {unscheduled.length === 0 ? (
                <div className="text-[11px] text-ink-muted" data-testid="drafts-unscheduled-empty">Everything has a date — nice and tidy.</div>
              ) : (
                <div className="flex flex-wrap gap-1.5" data-testid="drafts-unscheduled-tray">
                  {unscheduled.map((d) => (
                    <button key={d.post_id} onClick={() => openReview(d.post_id)}
                      draggable onDragStart={(e) => startDrag(e, d)}
                      data-testid={`drafts-unscheduled-${d.post_id}`}
                      className={`rounded-full border px-2.5 py-1 text-[10.5px] truncate max-w-[220px] transition cursor-grab active:cursor-grabbing hover:brightness-125 ${STATUS_STYLE[d.status] || STATUS_STYLE.draft}`}>
                      {d.title || d.topic || "Untitled"}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {review && (
        <ReviewModal
          post={review}
          onClose={() => { setReview(null); load(); }}
          onChanged={() => { setReview(null); load(); }}
        />
      )}
    </div>
  );
}
